import { categories } from "@sobidogam/catalog";
import {
	budgetPeriodFor,
	calculateLivingBudget,
	daysWithoutRecords,
	formatKstDate,
	formatWon,
	type HouseholdConsumptionState,
	hasLivingBudget,
	inPeriod,
	kstDateKey,
	resolveLivingBudgetPlan,
	resolvePeriod,
} from "@sobidogam/domain";
import {
	buildMemoryArchive,
	categoryShares,
	summarizeReports,
} from "@sobidogam/report";
import type { Receipt } from "@sobidogam/storage";
import { useState } from "react";
import { navigate } from "./chrome";
import {
	monthStartDayPref,
	readJsonPref,
	readPref,
	writeJsonPref,
	writePref,
} from "./prefs";
import { type TagDef, tagLabel } from "./tags";

type Track = (
	name: string,
	props: Record<string, string | number | boolean>,
) => void;

function nameOf(categoryId: string) {
	return categoryId === "__rest"
		? "그 밖"
		: (categories.find((category) => category.categoryId === categoryId)
				?.nameKo ?? "기타");
}

/** #37: amount share per category with the previous period beside it. */
export function CategoryChart({
	current,
	previous,
	previousLabel,
}: {
	current: Record<string, number> | undefined;
	previous: Record<string, number> | undefined;
	previousLabel: string;
}) {
	const rows = categoryShares(current, previous);
	if (!rows.length)
		return (
			<section className="report-card chart-card">
				<h2 className="card-title">어디에 많이 썼나</h2>
				<p className="field-hint">
					기록이 쌓이면 어디에 많이 썼는지 보여 드려요.
				</p>
			</section>
		);
	return (
		<section className="report-card chart-card" aria-label="분류별 지출 비율">
			<h2 className="card-title">어디에 많이 썼나</h2>
			<ul className="chart-list">
				{rows.map((row) => {
					const delta = row.amount - row.previousAmount;
					return (
						<li className="chart-row" key={row.categoryId}>
							{row.categoryId === "__rest" ? (
								<span className="chart-icon" aria-hidden="true" />
							) : (
								<img
									className="chart-icon"
									src={`/illustrations/${row.categoryId}.svg`}
									alt=""
									aria-hidden="true"
								/>
							)}
							<div className="chart-main">
								<span className="chart-name">
									{nameOf(row.categoryId)}
									<small>{row.ratio}%</small>
								</span>
								<svg
									className="chart-bar"
									viewBox="0 0 100 8"
									preserveAspectRatio="none"
									aria-hidden="true"
								>
									<rect
										x="0"
										y="0"
										width="100"
										height="8"
										rx="2"
										className="chart-track"
									/>
									<rect
										x="0"
										y="0"
										width={Math.max(1, row.ratio)}
										height="8"
										rx="2"
										className="chart-fill"
									/>
								</svg>
								<small className="chart-delta">
									{previousLabel} {formatWon(row.previousAmount)}
									{delta === 0
										? " · 같음"
										: delta > 0
											? ` · ${formatWon(delta)} 늘었어요`
											: ` · ${formatWon(-delta)} 줄었어요`}
								</small>
							</div>
							<b>{formatWon(row.amount)}</b>
						</li>
					);
				})}
			</ul>
		</section>
	);
}

/** #30: totals per tag inside the report period. */
export function TagTotals({
	receipts,
	tags,
	from,
	to,
}: {
	receipts: Receipt[];
	tags: TagDef[];
	from: Date;
	to: Date;
}) {
	const totals = new Map<string, { count: number; amount: number }>();
	for (const receipt of receipts) {
		if (receipt.deletedAt || !inPeriod(receipt.transactedAt, { from, to }))
			continue;
		for (const id of receipt.tagIds ?? []) {
			const current = totals.get(id) ?? { count: 0, amount: 0 };
			totals.set(id, {
				count: current.count + 1,
				amount: current.amount + Math.max(0, receipt.totalAmount),
			});
		}
	}
	const rows = [...totals.entries()].sort((a, b) => b[1].amount - a[1].amount);
	return (
		<section className="report-card">
			<h2 className="card-title">태그별</h2>
			{rows.length ? (
				<ul className="tag-totals">
					{rows.map(([id, value]) => (
						<li key={id}>
							<button
								type="button"
								onClick={() =>
									navigate(
										`/app/records?q=${encodeURIComponent(tagLabel(tags, id))}`,
									)
								}
							>
								<span>
									{tagLabel(tags, id)} <small>{value.count}건</small>
								</span>
								<b className="amount">{formatWon(value.amount)}</b>
							</button>
						</li>
					))}
				</ul>
			) : (
				<p className="field-hint">
					여행·경조사처럼 분류를 가로지르는 묶음은 기록할 때 태그로 남겨요.
				</p>
			)}
		</section>
	);
}

interface StatementCheckRecord {
	period: string;
	statedTotal: number;
	recordedTotal: number;
	checkedAt: string;
}

const STATEMENT_KEY = "sobidogam:statementChecks";

function readStatementChecks(): StatementCheckRecord[] {
	const value = readJsonPref<unknown>(STATEMENT_KEY, []);
	return Array.isArray(value)
		? value.filter(
				(check): check is StatementCheckRecord =>
					Boolean(check) &&
					typeof check.period === "string" &&
					Number.isSafeInteger(check.statedTotal) &&
					check.statedTotal >= 0,
			)
		: [];
}

export function statementMatched(
	period: string,
	recordedTotal: number,
): boolean {
	return readStatementChecks().some(
		(check) =>
			(check.period === period || check.period.startsWith(`${period}:`)) &&
			check.statedTotal === recordedTotal,
	);
}

/** #29: compare one number from the card app with the recorded total. */
export function StatementCheck({
	receipts,
	onTrack,
	onChange,
}: {
	receipts: Receipt[];
	onTrack: Track;
	onChange?: () => void;
}) {
	const [which, setWhich] = useState<"this_month" | "last_month">("this_month");
	const [checks, setChecks] = useState(readStatementChecks);
	const [drafts, setDrafts] = useState<Record<string, string>>({});
	const [notice, setNotice] = useState("");
	const period = resolvePeriod(which, new Date(), monthStartDayPref());
	const active = receipts.filter((receipt) => !receipt.deletedAt);
	const recorded = active
		.filter((receipt) => inPeriod(receipt.transactedAt, period))
		.reduce((sum, receipt) => sum + Math.max(0, receipt.totalAmount), 0);
	const periodKey = `${period.from.toISOString().slice(0, 10)}:${period.to.toISOString().slice(0, 10)}`;
	const legacyKey = period.from.toISOString().slice(0, 10);
	const saved = checks.find(
		(check) => check.period === periodKey || check.period === legacyKey,
	);
	const stated = drafts[periodKey] ?? (saved ? String(saved.statedTotal) : "");
	const checked = drafts[periodKey] === undefined && Boolean(saved);
	const statedNumber = Number(stated);
	const valid =
		stated !== "" && Number.isSafeInteger(statedNumber) && statedNumber >= 0;
	const diff = valid ? statedNumber - recorded : 0;
	const gaps = daysWithoutRecords(
		active.map((receipt) => receipt.transactedAt),
		period,
	);
	const persist = (next: StatementCheckRecord[]) => {
		if (!writeJsonPref(STATEMENT_KEY, next)) {
			setNotice(
				"저장하지 못했어요. 입력을 유지했으니 저장 공간을 확인하고 다시 시도해 주세요.",
			);
			return false;
		}
		setChecks(next);
		onChange?.();
		setDrafts((previous) => {
			const next = { ...previous };
			delete next[periodKey];
			return next;
		});
		setNotice("");
		return true;
	};
	return (
		<section
			className="report-card statement-card"
			aria-label="카드 명세와 맞춰 보기"
		>
			<h2 className="card-title">카드 명세와 맞춰 보기</h2>
			<fieldset className="chip-row">
				<legend className="sr-only">맞춰 볼 기간</legend>
				{(["this_month", "last_month"] as const).map((kind) => (
					<button
						key={kind}
						type="button"
						aria-pressed={which === kind}
						onClick={() => {
							setWhich(kind);
							setNotice("");
						}}
					>
						{kind === "this_month" ? "이번 달" : "지난달"}
					</button>
				))}
			</fieldset>
			<p className="field-hint">
				{period.label} · 기록 합계{" "}
				<b className="amount">{formatWon(recorded)}</b>
			</p>
			<label className="statement-input">
				카드 앱에서 본 합계
				<input
					inputMode="numeric"
					placeholder="예: 467300"
					value={stated}
					onChange={(event) => {
						setDrafts({
							...drafts,
							[periodKey]: event.target.value.replace(/[^0-9]/g, ""),
						});
						setNotice("");
					}}
				/>
			</label>
			<button
				type="button"
				className="button button-small"
				disabled={!valid}
				onClick={() => {
					const next = checks.filter(
						(check) => check.period !== periodKey && check.period !== legacyKey,
					);
					if (
						!persist(
							[
								...next,
								{
									period: periodKey,
									statedTotal: statedNumber,
									recordedTotal: recorded,
									checkedAt: new Date().toISOString(),
								},
							].slice(-24),
						)
					)
						return;
					onTrack("statement_check", {
						result: diff === 0 ? "match" : diff > 0 ? "missing" : "over",
					});
				}}
			>
				맞춰 보기
			</button>
			{saved ? (
				<button
					type="button"
					className="quiet-button"
					onClick={() => {
						if (persist(checks.filter((check) => check !== saved)))
							setDrafts((previous) => ({ ...previous, [periodKey]: "" }));
					}}
				>
					이 기간 대조 지우기
				</button>
			) : null}
			{notice ? <output className="notice">{notice}</output> : null}
			{checked && valid ? (
				<div className="statement-result" aria-live="polite">
					{diff === 0 ? (
						<strong>딱 맞아요. 이 기간은 맞춰 봄으로 표시했어요.</strong>
					) : diff > 0 ? (
						<>
							<strong>{formatWon(diff)}이 비어요.</strong>
							<p>
								{gaps.length
									? `기록 없는 날은 ${gaps
											.slice(-6)
											.map((day) => formatKstDate(`${day}T12:00:00+09:00`))
											.join(
												", ",
											)}${gaps.length > 6 ? ` 외 ${gaps.length - 6}일` : ""}이에요.`
									: "모든 날에 기록이 있어요. 하루에 두 번 이상 쓴 날을 확인해 보세요."}
							</p>
							<button
								type="button"
								className="button button-small"
								onClick={() => navigate("/app/capture")}
							>
								빠진 결제 붙여 넣기
							</button>
						</>
					) : (
						<>
							<strong>기록이 {formatWon(-diff)} 더 많아요.</strong>
							<p>취소된 결제를 지우지 않았는지 내역에서 확인해 보세요.</p>
						</>
					)}
					<small>
						참고용이에요. 할부·해외 결제·취소 시점 차이로 다를 수 있어요.
					</small>
				</div>
			) : null}
		</section>
	);
}

const MONTH_CLOSE_KEY = "sobidogam:monthCloseSeen";

/** #38: first visit after the month start day — last month on one card. */
export function MonthCloseCard({
	receipts,
	household,
}: {
	receipts: Receipt[];
	household: HouseholdConsumptionState;
}) {
	const monthStartDay = monthStartDayPref();
	const now = new Date();
	const current = budgetPeriodFor(now, monthStartDay);
	const last = budgetPeriodFor(
		new Date(current.start.getTime() - 1),
		monthStartDay,
	);
	const [seen, setSeen] = useState(() => readPref(MONTH_CLOSE_KEY));
	if (seen === last.key) return null;
	const active = receipts.filter((receipt) => !receipt.deletedAt);
	const lastRecords = active.filter((receipt) =>
		inPeriod(receipt.transactedAt, { from: last.start, to: last.end }),
	);
	if (!lastRecords.length) return null;
	const total = lastRecords.reduce(
		(sum, receipt) => sum + Math.max(0, receipt.totalAmount),
		0,
	);
	const plan = resolveLivingBudgetPlan(
		household,
		new Date(last.start.getTime() + 86_400_000),
		monthStartDay,
	);
	const budget =
		plan.monthStartDay === monthStartDay && hasLivingBudget(household, plan)
			? calculateLivingBudget(
					active,
					plan,
					new Date(last.start.getTime() + 86_400_000),
				)
			: null;
	const byCategory = new Map<string, number>();
	for (const receipt of lastRecords)
		byCategory.set(
			receipt.categoryId,
			(byCategory.get(receipt.categoryId) ?? 0) + receipt.totalAmount,
		);
	const top = [...byCategory.entries()].sort((a, b) => b[1] - a[1]).slice(0, 3);
	const big = lastRecords
		.filter(
			(receipt) =>
				!receipt.sensitiveMasked && !receipt.afterNote && !receipt.satisfaction,
		)
		.sort((a, b) => b.totalAmount - a.totalAmount)
		.slice(0, 3);
	const close = () => {
		writePref(MONTH_CLOSE_KEY, last.key);
		setSeen(last.key);
	};
	return (
		<section className="month-close-card receipt-edge" aria-label="지난달 마감">
			<div className="month-close-head">
				<h2>{Number(last.key.slice(5))}월 마감</h2>
				<button type="button" className="sheet-close" onClick={close}>
					닫기
				</button>
			</div>
			<p className="month-close-total">
				<b className="amount">{formatWon(total)}</b>
				<span>{lastRecords.length}건</span>
			</p>
			<p className="field-hint">
				{budget
					? `저장된 생활비 3묶음 한도 ${formatWon(budget.totalLimit)} · 해당 지출 ${formatWon(budget.totalSpent)} · ${budget.totalSpent <= budget.totalLimit ? `${formatWon(budget.totalLimit - budget.totalSpent)} 남았어요` : `${formatWon(budget.totalSpent - budget.totalLimit)} 넘었어요`}`
					: "이 기간의 생활비 한도는 보존되어 있지 않아 비교할 수 없어요. 현재 한도를 과거에 적용하지 않아요."}
			</p>
			<ol className="month-close-top">
				{top.map(([categoryId, amount]) => (
					<li key={categoryId}>
						<span>{nameOf(categoryId)}</span>
						<b className="amount">{formatWon(amount)}</b>
					</li>
				))}
			</ol>
			{big.length ? (
				<div className="month-close-big">
					<h3>돌아볼 큰 지출</h3>
					{big.map((receipt) => (
						<button
							key={receipt.receiptId}
							type="button"
							onClick={() =>
								navigate(`/app/records?reflect=${receipt.receiptId}`)
							}
						>
							<span>
								{receipt.storeDisplay}{" "}
								<small>{formatKstDate(receipt.transactedAt)}</small>
							</span>
							<b className="amount">{formatWon(receipt.totalAmount)}</b>
						</button>
					))}
				</div>
			) : null}
		</section>
	);
}

/** #40 (세지 않음): one year / one month ago today, only when a record exists. */
export function OnThisDay({ receipts }: { receipts: Receipt[] }) {
	const today = kstDateKey(new Date());
	const [year, month, day] = today.split("-").map(Number) as [
		number,
		number,
		number,
	];
	const yearAgo = `${year - 1}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
	const monthAgoDate = new Date(Date.UTC(year, month - 2, day));
	const monthAgo = monthAgoDate.toISOString().slice(0, 10);
	const pick = (key: string) =>
		receipts.find(
			(receipt) =>
				!receipt.deletedAt && kstDateKey(receipt.transactedAt) === key,
		);
	const hit = pick(yearAgo)
		? { label: "1년 전 오늘", receipt: pick(yearAgo) }
		: pick(monthAgo)
			? { label: "한 달 전 오늘", receipt: pick(monthAgo) }
			: null;
	if (!hit?.receipt) return null;
	return (
		<button
			type="button"
			className="on-this-day"
			onClick={() => navigate(`/app/records?reflect=${hit.receipt?.receiptId}`)}
		>
			<small>{hit.label}</small>
			<span>
				{hit.receipt.storeDisplay} ·{" "}
				<b className="amount">{formatWon(hit.receipt.totalAmount)}</b>
			</span>
			{hit.receipt.memo ? <em>“{hit.receipt.memo}”</em> : null}
		</button>
	);
}

/** #41 (세지 않음): the year on one free screen. PDF stays paid (8절). */
export function AnnualPreview({
	receipts,
	onPdf,
}: {
	receipts: Receipt[];
	onPdf: () => void;
}) {
	const year = Number(kstDateKey(new Date()).slice(0, 4));
	const archive = buildMemoryArchive(receipts, year);
	const annual = summarizeReports(
		receipts,
		"year",
		new Date(),
		monthStartDayPref(),
	).current;
	if (archive.recordCount < 10)
		return (
			<section className="report-card annual-preview">
				<h2 className="card-title">{year}년 결산 미리 보기</h2>
				<p className="field-hint">
					10건부터 결산이 그려져요 (지금 {archive.recordCount}건).
				</p>
			</section>
		);
	const top = categoryShares(annual.categoryAmounts, undefined, 3);
	return (
		<section
			className="report-card annual-preview receipt-edge"
			aria-label={`${year}년 결산`}
		>
			<h2 className="card-title">{year}년 결산 미리 보기</h2>
			<p className="annual-total">
				<b className="amount">{formatWon(annual.totalAmount ?? 0)}</b>
				<span>
					{archive.recordCount}건 · 열린 칸 {annual.unlockedCount}개
				</span>
			</p>
			<dl className="annual-facts">
				<div>
					<dt>많이 쓴 곳</dt>
					<dd>{top.map((row) => nameOf(row.categoryId)).join(", ") || "—"}</dd>
				</div>
				<div>
					<dt>다시 찾은 곳</dt>
					<dd>
						{archive.topRevisitedPlaces
							.slice(0, 3)
							.map((place) => `${place.storeDisplay} ${place.visits}번`)
							.join(", ") || "아직 없어요"}
					</dd>
				</div>
				<div>
					<dt>가장 많이 움직인 계절</dt>
					<dd>{archive.mostActiveSeason ?? "—"}</dd>
				</div>
			</dl>
			{archive.moments[0] ? (
				<blockquote className="annual-moment">
					“{archive.moments[0].memo}”
					<small>
						{archive.moments[0].date} · {archive.moments[0].storeDisplay}
					</small>
				</blockquote>
			) : null}
			<button type="button" className="quiet-button full-width" onClick={onPdf}>
				인쇄용 PDF로 저장 (기억책)
			</button>
		</section>
	);
}
